import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// TODO(STP-459): this drives a module-level connection singleton, so two surfaces mounting
// presence at once would fight over init/stop. Fine while the loan editor is the only mount.
import { accessTokenFactory, loanApplicationSignalRConnection } from 'app/services/signalR';

import { SIGNAL_R_LOAN_APPLICATION_CONNECTION_URL } from 'constants/api';
import {
  HEARTBEAT_INTERVAL_MS,
  PresenceEntityType,
  PresenceSignalRMessageType,
} from 'constants/presence';
import useActivityTracking from 'hooks/useActivityTracking';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { logger } from 'utils/logger';
import { buildPresenceEntityRef } from 'utils/presence';

import { selectMasterCompanyId, selectUserData } from 'store/auth/selectors';
import {
  clearPresenceStateAction,
  resetPresenceAction,
  setPresenceConnectedAction,
} from 'store/presence/actions';

/**
 * Owns the entity-generic presence connection lifecycle: SignalR connect, join, heartbeat,
 * rejoin on reconnect/wake, and leave-on-unmount. Any entity-specific wiring the caller still
 * needs on the SAME connection (e.g. the loan editor's own listener registration) hooks in via
 * the on* callbacks below, fired at the same points that wiring previously ran inline in
 * LoanApplicationSignalRContainer — connect → onConnectionReady → join → onJoined, and
 * leave → onLeaving → disconnect → onLeft.
 *
 * The join/heartbeat/leave/rejoin calls send the entity reference in the existing first
 * argument (`buildPresenceEntityRef` — STP-456's hub contract). No new positional argument is
 * introduced, and loans still send a bare Guid so this works against a pre-STP-456 hub too.
 *
 * @param {Object} params
 * @param {string} [params.entityType] - kind of entity being viewed ('Loan' today).
 * @param {string} params.entityId - id of the entity being viewed.
 * @param {Function} [params.onConnectionReady] - called once the transport is up, before join.
 * @param {Function} [params.onJoined] - called once join, heartbeat, and rejoin are wired.
 * @param {Function} [params.onLeaving] - called after the leave invoke, before teardown.
 * @param {Function} [params.onLeft] - called at the very end of teardown.
 * @param {string|number|null} [params.reinitKey] - caller state the connection is built from.
 *   The on* callbacks live in refs, so a change inside them cannot re-run the connect effect;
 *   pass anything the callbacks close over that must tear the connection down and re-run them.
 */
const usePresence = ({
  entityType = PresenceEntityType.LOAN,
  entityId,
  reinitKey = null,
  onConnectionReady,
  onJoined,
  onLeaving,
  onLeft,
}) => {
  const companyId = useSelector(selectMasterCompanyId);
  const userData = useSelector(selectUserData);

  const dispatch = useDispatch();

  const wireEntityId = buildPresenceEntityRef(entityType, entityId);

  const heartbeatIntervalRef = useRef(null);
  const onConnectionReadyRef = useRef(onConnectionReady);
  const onJoinedRef = useRef(onJoined);
  const onLeavingRef = useRef(onLeaving);
  const onLeftRef = useRef(onLeft);

  const { isUserActive } = useActivityTracking();

  const startHeartbeat = useCallback(
    currentWireEntityId => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }

      heartbeatIntervalRef.current = setInterval(async () => {
        const isVisible = document.visibilityState === 'visible';

        try {
          await loanApplicationSignalRConnection.invoke(
            PresenceSignalRMessageType.HEARTBEAT,
            currentWireEntityId,
            isVisible && isUserActive(),
            null,
          );
        } catch (err) {
          logger.error('Failed to send presence heartbeat', {
            entityType,
            entityId: currentWireEntityId,
            err,
          });
        }
      }, HEARTBEAT_INTERVAL_MS);
    },
    [entityType, isUserActive],
  );

  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  const initCallback = useCallback(async () => {
    loanApplicationSignalRConnection.init(
      SIGNAL_R_LOAN_APPLICATION_CONNECTION_URL,
      accessTokenFactory,
    );

    await loanApplicationSignalRConnection.start();

    await onConnectionReadyRef.current?.();

    try {
      await loanApplicationSignalRConnection.invoke(
        PresenceSignalRMessageType.JOIN_LOAN_PRESENCE,
        wireEntityId,
        companyId,
        userData?.displayName || null,
        userData?.firstName || null,
        userData?.lastName || null,
        null,
      );
    } catch (err) {
      logger.error('Failed to join presence', { entityType, entityId, err });
    }

    dispatch(setPresenceConnectedAction(true));
    startHeartbeat(wireEntityId);

    const rejoinPresence = async () => {
      dispatch(clearPresenceStateAction());

      try {
        await loanApplicationSignalRConnection.invoke(
          PresenceSignalRMessageType.JOIN_LOAN_PRESENCE,
          wireEntityId,
          companyId,
          userData?.displayName || null,
          userData?.firstName || null,
          userData?.lastName || null,
          null,
        );
      } catch (err) {
        logger.error('Failed to rejoin presence after reconnection', {
          entityType,
          entityId,
          err,
        });
      }
    };

    loanApplicationSignalRConnection.onreconnected(rejoinPresence);
    loanApplicationSignalRConnection.onWakeReconnected(rejoinPresence);

    await onJoinedRef.current?.();
  }, [companyId, dispatch, entityId, entityType, startHeartbeat, userData, wireEntityId]);

  // Refs always hold the latest callback so the connect/disconnect effect below can stay keyed
  // on entityId alone — updated here (post-render) rather than during render, per the
  // rules-of-hooks "no ref mutation while rendering" guard.
  useEffect(() => {
    onConnectionReadyRef.current = onConnectionReady;
    onJoinedRef.current = onJoined;
    onLeavingRef.current = onLeaving;
    onLeftRef.current = onLeft;
  });

  useEffectSafeStrictMode(() => {
    if (!wireEntityId) {
      return;
    }

    initCallback();

    return async () => {
      stopHeartbeat();

      try {
        await loanApplicationSignalRConnection.invoke(
          PresenceSignalRMessageType.LEAVE_LOAN_PRESENCE,
          wireEntityId,
          null,
        );
      } catch (err) {
        logger.error('Failed to leave presence', { entityType, entityId, err });
      }

      await onLeavingRef.current?.();

      await loanApplicationSignalRConnection.stop();
      loanApplicationSignalRConnection.resetConnection();

      dispatch(resetPresenceAction());

      await onLeftRef.current?.();
    };
  }, [initCallback, dispatch, entityId, entityType, reinitKey, stopHeartbeat, wireEntityId]);
};

export default usePresence;
